import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { ActionType, type Chart, type Crosshair, type KLineData } from 'klinecharts'
import { getIndicatorDef, type LegendValueFormat } from '@edge/charts-indicators'
import type { ActiveIndicator } from '../components/IndicatorLegend'
import type { EventBus } from '../core/EventBus'
import { CANDLE_PANE_ID } from '../types/chart'
import { withAlphaOrGray } from '../utils/color'

export interface IndicatorFigureValue {
  title: string
  value: string
  color: string
}

/**
 * Resolve the legend text color for one indicator figure value.
 *
 * The VOLUME BAR reads like the OHLC header — tinted by the current bar's
 * direction (green up / red down) to match its own histogram bar. But VOL's
 * moving-average LINES are not directional: their legend text must match the
 * drawn line, so they fall through the normal chain (per-figure user override →
 * catalog default → global palette by position). Gating on `fig.key` rather
 * than on `ind.name === 'VOL'` alone is the fix — the old branch painted every
 * VOL figure (bar AND MA) with the bar-direction color, so the MA value never
 * showed its line color.
 */
export function resolveFigureColor(params: {
  indName: string
  figKey: string
  barDirColor: string
  instanceStyleColor?: string
  catalogDefaultColor?: string
  paletteColor?: string
  /** The volume indicator's OWN direction color (user Style-tab pick), if any —
   *  so the legend swatch tracks the picker instead of the candle direction. */
  volBarColor?: string
}): string {
  const { indName, figKey, barDirColor, volBarColor } = params
  if (indName === 'VOL' && figKey === 'volume') return volBarColor ?? barDirColor
  return params.instanceStyleColor ?? params.catalogDefaultColor ?? params.paletteColor ?? '#929AA5'
}

/** Compact readout: 18,850,000 → "18.85M", keeping small values precise. */
function compactValue(v: number): string {
  const a = Math.abs(v)
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (a >= 1e3) return `${(v / 1e3).toFixed(2)}K`
  return v.toFixed(2)
}

/**
 * A legend value. Without a format, the compact readout. A script's Pine format and
 * precision (`fmt`): format.price prints the value in full (2 decimals unless a precision
 * says), format.percent adds "%", format.volume abbreviates (K, M, B), format.mintick is
 * the tick precision (2 decimals, 4 under 1.00); a precision alone prints in full at it.
 */
export function formatIndicatorValue(v: number, fmt?: LegendValueFormat): string {
  const precision = fmt?.precision
  switch (fmt?.format) {
    case 'price':
      return v.toFixed(precision ?? 2)
    case 'percent':
      return `${v.toFixed(precision ?? 2)}%`
    case 'volume':
      return compactValue(v)
    case 'mintick':
      return v.toFixed(precision ?? (Math.abs(v) < 1 ? 4 : 2))
    default:
      return precision !== undefined ? v.toFixed(precision) : compactValue(v)
  }
}

/** Deep-equal on the displayed readouts — computed maps are always fresh
 *  objects, so identity alone would re-render the legend per crosshair move
 *  even when every visible value is unchanged. */
function sameValues(
  a: Map<string, IndicatorFigureValue[]>,
  b: Map<string, IndicatorFigureValue[]>,
): boolean {
  if (a.size !== b.size) return false
  for (const [key, av] of a) {
    const bv = b.get(key)
    if (!bv || bv.length !== av.length) return false
    for (let i = 0; i < av.length; i++) {
      if (
        av[i].title !== bv[i].title ||
        av[i].value !== bv[i].value ||
        av[i].color !== bv[i].color
      ) {
        return false
      }
    }
  }
  return true
}

function indicatorFigureValues(
  chart: Chart,
  ind: ActiveIndicator,
  idx: number,
  bar: KLineData,
  barDirColor: string,
  lineColors: Array<{ color?: string }> | undefined,
): IndicatorFigureValue[] {
  try {
    const result = chart.getIndicatorByPaneId(ind.paneId, ind.instanceName ?? ind.name) as {
      figures?: Array<{ key: string; title?: string }>
      result?: Array<Record<string, number>>
      styles?: {
        bars?: Array<{
          upColor?: string
          downColor?: string
          noChangeColor?: string
          plotType?: string
        }>
      }
    } | null

    if (!result?.figures || !result?.result) return []

    const row = result.result[idx]
    if (!row) return []

    // Catalog definition — its per-figure defaultColor is what the chart
    // actually draws with (via buildIndicatorStyles), so the readout must
    // consult it before the global palette or the two colors diverge
    // (e.g. EMA draws catalog purple while the palette leads with orange).
    const def = getIndicatorDef(ind.name)

    // Volume legend swatch follows the VOLUME indicator's own bar colors
    // (the user's Style-tab pick), not the candle direction. Force full
    // opacity so the small readout stays legible even though the drawn
    // bars are semi-transparent (their stored color carries the 0.5 alpha).
    // Volume drawn as a line (Style tab Plot type) has one color, not two.
    const volBars = result.styles?.bars?.[0]
    const volBarColor = volBars
      ? withAlphaOrGray(
          (volBars.plotType === 'line'
            ? volBars.noChangeColor
            : bar.close >= bar.open
              ? volBars.upColor
              : volBars.downColor) ?? barDirColor,
          1,
        )
      : undefined

    const figureValues: IndicatorFigureValue[] = []
    result.figures.forEach((fig, figIdx) => {
      const val = row[fig.key]
      if (val == null || isNaN(val)) return
      // a script's Pine format, precision, and display without the status line
      const fmt = ind.valueFormats?.[fig.key]
      if (fmt?.hidden) return
      // Per-figure style override wins (user edits + external/debug
      // indicators carry their real line colors here); then the catalog
      // default for this figure; then the global palette by position.
      // The volume BAR follows the bar direction like the OHLC values; its
      // MA lines follow their own line color (see resolveFigureColor). A
      // figure colored per bar (a script plot's series color) takes its
      // color on the shown bar.
      const color =
        ind.figureColorAt?.(fig.key, bar.timestamp) ??
        resolveFigureColor({
          indName: ind.name,
          figKey: fig.key,
          barDirColor,
          instanceStyleColor: ind.styles?.[fig.key]?.color,
          catalogDefaultColor: def?.figures.find((f) => f.key === fig.key)?.defaultColor,
          paletteColor: lineColors?.[figIdx]?.color,
          volBarColor,
        })
      figureValues.push({
        title: fig.title ?? fig.key,
        value: formatIndicatorValue(val, fmt),
        color,
      })
    })

    // VOL in overlay mode exposes no figures (they'd pollute the candle
    // pane's y-axis range) but its calc still produces `volume` rows —
    // surface that so the legend keeps its "Vol: …" readout.
    if (figureValues.length === 0 && ind.name === 'VOL' && ind.paneId === CANDLE_PANE_ID) {
      const vol = row.volume
      if (vol != null && !isNaN(vol)) {
        figureValues.push({
          title: 'Vol: ',
          value: formatIndicatorValue(vol),
          color: barDirColor,
        })
      }
    }

    return figureValues
  } catch {
    // Indicator may not be ready yet
    return []
  }
}

export function useIndicatorValues(
  chart: Chart | null,
  indicators: ActiveIndicator[],
  eventBus: EventBus | null,
): Map<string, IndicatorFigureValue[]> {
  const [values, setValues] = useState<Map<string, IndicatorFigureValue[]>>(() => new Map())
  const indicatorsRef = useRef(indicators)
  useLayoutEffect(() => {
    indicatorsRef.current = indicators
  })
  /** Recompute at the latest bar with the async-calc retries; set by the main effect. */
  const refreshRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!chart) return

    const compute = (dataIndex?: number) => {
      const dataList = chart.getDataList()
      if (dataList.length === 0) return

      const idx = dataIndex ?? dataList.length - 1
      if (idx < 0 || idx >= dataList.length) return

      const inds = indicatorsRef.current.filter((i) => i.visible)
      const next = new Map<string, IndicatorFigureValue[]>()

      // Get indicator line colors from chart styles
      const styles = chart.getStyles()
      const lineColors = (styles?.indicator as Record<string, unknown[]> | undefined)?.lines as
        | Array<{ color?: string }>
        | undefined

      // Volume reads like the OHLC header: tinted by the bar's direction
      // (matching its own histogram bar), not by the line palette.
      const bar = dataList[idx]
      const candleBar = (styles?.candle as { bar?: { upColor?: string; downColor?: string } })?.bar
      const barDirColor =
        bar.close >= bar.open
          ? (candleBar?.upColor ?? '#26a69a')
          : (candleBar?.downColor ?? '#ef5350')

      for (const ind of inds) {
        const figureValues = indicatorFigureValues(chart, ind, idx, bar, barDirColor, lineColors)
        if (figureValues.length > 0) next.set(ind.key, figureValues)
      }

      setValues((prev) => (sameValues(prev, next) ? prev : next))
    }

    // OnCrosshairChange and realtime ticks fire far faster than the display
    // needs; coalesce to one compute per animation frame (last event wins).
    // `hasPending` (not `pendingIdx == null`) tracks scheduling because
    // undefined is a meaningful "latest bar" request.
    let computeRaf: number | null = null
    let pendingIdx: number | undefined
    let hasPending = false
    const scheduleCompute = (dataIndex?: number) => {
      pendingIdx = dataIndex
      hasPending = true
      if (computeRaf != null) return
      computeRaf = requestAnimationFrame(() => {
        computeRaf = null
        if (hasPending) {
          hasPending = false
          compute(pendingIdx)
        }
      })
    }

    // Crosshair change handler
    const onCrosshair = (crosshair: Crosshair) => {
      scheduleCompute(crosshair.dataIndex ?? undefined)
    }

    chart.subscribeAction(ActionType.OnCrosshairChange, onCrosshair)

    // Also compute on data loads/updates (for latest bar when no crosshair is
    // active), and snap back to the latest bar when the cursor leaves the
    // chart. `data:loaded` covers history loads — including in-place symbol
    // swaps (scanner ticker propagation) — while `data:updated` is realtime
    // ticks only; listening to just the latter left fresh tickers blank until
    // the first hover.
    let unsubLoaded: (() => void) | undefined
    let unsubData: (() => void) | undefined
    let unsubLeave: (() => void) | undefined
    let unsubRemote: (() => void) | undefined
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    let retryRaf = 0
    // Bars (or a recalc) land before klinecharts finishes its async indicator
    // calc, so a synchronous compute reads stale or empty results. Recompute now
    // for the fast path, then next frame and once more shortly after, so fresh
    // tickers and just-added / re-based indicators read without a hover.
    const refreshWithRetry = () => {
      compute()
      cancelAnimationFrame(retryRaf)
      retryRaf = requestAnimationFrame(() => compute())
      clearTimeout(retryTimer)
      retryTimer = setTimeout(() => compute(), 150)
    }
    refreshRef.current = refreshWithRetry
    if (eventBus) {
      unsubLoaded = eventBus.on('data:loaded', () => refreshWithRetry())
      unsubData = eventBus.on('data:updated', () => scheduleCompute())
      unsubLeave = eventBus.on('crosshair:leave', () => scheduleCompute())
      // Synced crosshair from a PEER chart: follow the mapped bar; BLANK the
      // values when the timestamp lands in a gap here (dataIndex null); back
      // to the latest bar when the peer clears (payload null).
      unsubRemote = eventBus.on('crosshair:remote', (payload) => {
        const p = payload as { dataIndex: number | null } | null
        if (p === null) scheduleCompute()
        else if (p.dataIndex == null) {
          // Drop any scheduled compute so a stale local crosshair event can't
          // repaint values right after the blank.
          hasPending = false
          setValues((prev) => (prev.size === 0 ? prev : new Map()))
        } else scheduleCompute(p.dataIndex)
      })
    }

    // Initial computation
    compute()

    return () => {
      refreshRef.current = null
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCrosshair)
      unsubLoaded?.()
      unsubData?.()
      unsubLeave?.()
      unsubRemote?.()
      if (computeRaf != null) cancelAnimationFrame(computeRaf)
      cancelAnimationFrame(retryRaf)
      clearTimeout(retryTimer)
    }
  }, [chart, eventBus])

  // The indicator set changed (added, removed, relabelled, shown): no data event
  // fires for that, so without this a new or re-based row reads blank or stale
  // until the next hover.
  const indicatorsKey = indicators.map((i) => `${i.key}|${i.label}|${i.visible}`).join('\n')
  useEffect(() => {
    refreshRef.current?.()
  }, [indicatorsKey])

  return values
}
